import * as React from 'react'
import {
  IdKeyedObjectShape,
  LoadingState,
  CustomerSummary,
  LoadingStatus,
} from './base'
import {
  LicensedAgencyViewModel,
  CustomerInterfaceModel,
  InterfaceFeature,
  CustomerDetailsViewModel,
  InterfaceHealthColor,
  Agency,
  ImportConfigurationViewModel,
  GlobalConfiguration,
} from '../api'
import { IdentifiableModel } from 'cs-kevlar-react/lib/es/base'
import { idObjectToArray } from 'utils'

export type CustomerInterfaceWithImportConfiguration = {
  importConfiguration: ImportConfigurationViewModel | null
}

export type CustomerInterface = Omit<
  CustomerInterfaceModel,
  'importConfiguration'
> &
  CustomerInterfaceWithImportConfiguration &
  IdentifiableModel


export type LicensedAgencyWithGlobalConfiguration = {
  globalConfiguration: GlobalConfiguration | null
}

export type LicensedAgency = Omit<LicensedAgencyViewModel, 'globalConfiguration'> & LicensedAgencyWithGlobalConfiguration & IdentifiableModel

export interface CustomerState {
  customer: CustomerSummary
  connectors: IdKeyedObjectShape<CustomerInterface>
  connectorList: CustomerInterface[]
  agencies: IdKeyedObjectShape<LicensedAgency>
  agencyList: LicensedAgency[]
  agencyDocuments: Agency[]
  hasOwnerAgencies: boolean
  showConnectorDataSearch: boolean
  enabledFeatures: InterfaceFeature[]
}

export interface CurrentCustomerState {
  currentCustomer: CustomerState | null
  loadingState: LoadingState
}

type LoadCustomerDetails = {
  type: 'LoadCustomerDetails'
  payload: CustomerDetailsViewModel
  timestamp: number | null
}

type UpdateConnectorHealthInfo = {
  type: 'UpdateConnectorHealthInfo'
  payload: {
    [key: string]: InterfaceHealthColor
  }
}

type ConnectorConfiguration = {
  customerInterfaceId: string
  configuration: {
    [key: string]: string
  }
}

type UpdateConnectorConfiguration = {
  type: 'UpdateConnectorConfiguration'
  payload: ConnectorConfiguration
}

type EditLicensedAgency = {
  type: 'EditLicensedAgency'
  payload: LicensedAgency
}

type AddLicensedAgency = {
  type: 'AddLicensedAgency'
  payload: LicensedAgency
}

type DeleteLicensedAgency = {
  type: 'DeleteLicensedAgency'
  payload: string
}

type EditConnector = {
  type: 'EditConnector'
  payload: CustomerInterface
}

type AddConnector = {
  type: 'AddConnector'
  payload: CustomerInterface
}

type DeleteConnector = {
  type: 'DeleteConnector'
  payload: string
}

type ClearAction = {
  type: 'Clear'
}

type SetLoadingState = {
  type: 'SetLoadingState'
  payload: LoadingStatus
}

export type CustomerAction =
  | LoadCustomerDetails
  | ClearAction
  | UpdateConnectorHealthInfo
  | EditLicensedAgency
  | DeleteLicensedAgency
  | AddLicensedAgency
  | EditConnector
  | AddConnector
  | DeleteConnector
  | SetLoadingState
  | UpdateConnectorConfiguration

export interface CurrentCustomerContextState {
  state: CurrentCustomerState
  dispatch: (action: CustomerAction) => void
}

export const DEFAULT_CUSTOMER_STATUS: CurrentCustomerContextState = {
  state: {
    currentCustomer: null,
    loadingState: {
      status: 'None',
      lastApiReceiveAt: null,
    },
  } as CurrentCustomerState,
  dispatch: (action: CustomerAction) => {},
}

const reduceArrayToObject: <TType extends IdentifiableModel>(
  items: TType[],
) => IdKeyedObjectShape<TType> = (items) => {
  return items.reduce((accum, row) => ({ ...accum, [row.id]: row }), {})
}

const reducer = (state: CurrentCustomerState, action: CustomerAction) => {
  switch (action.type) {
    case 'Clear': {
      return DEFAULT_CUSTOMER_STATUS.state
    }
    case 'SetLoadingState': {
      return {
        ...state,
        loadingState: {
          ...state.loadingState,
          status: action.payload,
        },
      }
    }
    case 'UpdateConnectorHealthInfo': {
      if (state.currentCustomer === null) return state

      const updatedConnectorList = state.currentCustomer.connectorList.map(
        (connector) => ({
          ...connector,
          healthState: action.payload[connector.id],
        }),
      )

      return {
        ...state,
        currentCustomer: {
          ...state.currentCustomer,
          connectorList: updatedConnectorList,
          connectors: reduceArrayToObject(
            updatedConnectorList.map((connector) => ({
              ...connector,
              id: connector.customerInterfaceId as string,
            })),
          ),
        },
      }
    }
    case 'UpdateConnectorConfiguration': {
      if (state.currentCustomer === null) return state

      const updatedConnectorList = state.currentCustomer.connectorList.map(
        (connector) => {
          if (
            connector.customerInterfaceId === action.payload.customerInterfaceId
          ) {
            return {
              ...connector,
              configurationOverrides: action.payload.configuration,
            }
          }

          return {
            ...connector,
          }
        },
      )

      return {
        ...state,
        currentCustomer: {
          ...state.currentCustomer,
          connectorList: updatedConnectorList,
          connectors: reduceArrayToObject(
            updatedConnectorList.map((connector) => ({
              ...connector,
              id: connector.customerInterfaceId as string,
            })),
          ),
        },
      }
    }
    case 'DeleteConnector': {
      if (state.currentCustomer === null) return state

      const newConnectorList = [
        ...state.currentCustomer.connectorList.filter(
          (connector) => connector.id !== action.payload,
        ),
      ]

      return {
        ...state,
        currentCustomer: {
          ...state.currentCustomer,
          connectorList: newConnectorList,
          connectors: reduceArrayToObject(newConnectorList),
        },
      }
    }
    case 'EditConnector': {
      if (state.currentCustomer === null) return state

      const newConnectorList = state.currentCustomer.connectorList.map(
        (connector) => {
          if (connector.id === action.payload.id) return action.payload

          return connector
        },
      )

      return {
        ...state,
        currentCustomer: {
          ...state.currentCustomer,
          connectorList: newConnectorList,
          connectors: reduceArrayToObject(newConnectorList),
        },
      }
    }
    case 'AddConnector': {
      if (state.currentCustomer === null) return state

      const newConnectorList = [
        ...state.currentCustomer.connectorList,
        action.payload,
      ]

      return {
        ...state,
        currentCustomer: {
          ...state.currentCustomer,
          connectorList: newConnectorList,
          connectors: reduceArrayToObject(newConnectorList),
        },
      }
    }
    case 'DeleteLicensedAgency': {
      if (state.currentCustomer === null) return state

      const newAgenciesList = [
        ...state.currentCustomer.agencyList.filter(
          (agency) => agency.id !== action.payload,
        ),
      ]

      return {
        ...state,
        currentCustomer: {
          ...state.currentCustomer,
          agencyList: newAgenciesList,
          agencies: reduceArrayToObject(newAgenciesList),
        },
      }
    }
    case 'EditLicensedAgency': {
      if (state.currentCustomer === null) return state

      const newAgenciesList = state.currentCustomer.agencyList.map((agency) => {
        if (agency.id === action.payload.id) return action.payload

        return agency
      })

      return {
        ...state,
        currentCustomer: {
          ...state.currentCustomer,
          agencyList: newAgenciesList,
          agencies: reduceArrayToObject(newAgenciesList),
        },
      }
    }
    case 'AddLicensedAgency': {
      if (state.currentCustomer === null) return state

      const newAgenciesList = [
        ...state.currentCustomer.agencyList,
        action.payload,
      ]

      return {
        ...state,
        currentCustomer: {
          ...state.currentCustomer,
          agencyList: newAgenciesList,
          agencies: reduceArrayToObject(newAgenciesList),
        },
      }
    }
    case 'LoadCustomerDetails': {
      const { connectors, agencies, customer, ...rest } = action.payload

      const connectorMap = reduceArrayToObject(
        action.payload.connectors.map((connector) => ({
          ...connector,
          id: connector.customerInterfaceId as string,
        })),
      )

      const agencyMap = reduceArrayToObject(
        action.payload.agencies.map((agency) => ({
          ...agency,
          id: agency.licensedAgencyId,
        })),
      )

      return {
        ...state,
        loadingState: {
          status: 'None',
          lastApiReceiveAt: action.timestamp,
        },
        currentCustomer: {
          connectors: connectorMap,
          connectorList: idObjectToArray(connectorMap),
          agencies: agencyMap,
          agencyList: idObjectToArray(agencyMap),
          customer: {
            ...customer,
            id: customer.customerAgencyId,
          },
          ...rest,
        },
      }
    }
    default:
      return state
  }
}

export const getInitialCustomer = () => DEFAULT_CUSTOMER_STATUS.state

export const CurrentCustomerContext = React.createContext<
  CurrentCustomerContextState
>(DEFAULT_CUSTOMER_STATUS)

type CurrentCustomerContextProviderProps = {
  children: any
}

export const CurrentCustomerContextProvider: React.FunctionComponent<CurrentCustomerContextProviderProps> = ({
  children,
}) => {
  const initialCustomerState = getInitialCustomer()
  const [state, dispatch] = React.useReducer(
    reducer as React.Reducer<CurrentCustomerState, CustomerAction>,
    initialCustomerState,
  )
  const value = { state, dispatch }

  return (
    <CurrentCustomerContext.Provider value={value}>
      {children}
    </CurrentCustomerContext.Provider>
  )
}

export const CurrentCustomerContextConsumer = CurrentCustomerContext.Consumer
